import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import type { ConciergeHeldFlight } from '@voll-intelligence/genui-contract';
import type { TravelerDirectoryPort, TravelerPhotosPort } from '../schema/traveler_directory.js';
import type { FlightSegmentEnrichmentPort } from './flight_enrichment.js';
import type { GenUiTelemetryPort } from './telemetry.js';

/*
 * O núcleo não conhece os kinds (#5228). Aqui fica só o que o próprio núcleo lê
 * ou o que mais de um domínio usa; o resto é declarado pelo domínio dono, que
 * estende `GenUiConfig` e `GenUiPorts` por declaration merging no seu
 * `genui_config.ts` (ex.: `flight/shared/genui_config.ts`). O `react.tsx`, a
 * mesma composition root do `REGISTRY`, importa cada um desses arquivos: é isso
 * que põe o pedaço de cada domínio no programa de quem só importa a entrada.
 */

/**
 * Ports de leitura (query) injetados pelo host. Cada um é opcional: ausente =
 * null object (o card/drawer degrada). Só IO de leitura idempotente — o submit
 * sai por `dispatch` (comando). Os ports de cada domínio entram pelo
 * `genui_config.ts` dele.
 */
export interface GenUiPorts {
  /** Busca paginada de viajantes do campo QUEM — compartilhado entre planners. */
  travelers?: TravelerDirectoryPort;
  /** A foto de cada viajante pelo id (GET /traveler-photos) — o resumo do checkout. */
  travelerPhotos?: TravelerPhotosPort;
  /** Enriquecimento Cirium do trecho dos CARDS (GET /flight/segment-enrichment),
   *  keyed por identidade do voo. Ausente ⇒ drawer seco (console do operador). */
  flightSegmentEnrichment?: FlightSegmentEnrichmentPort;
}

/**
 * Configuração de runtime injetada pelo app hospedeiro nos renderers do kit.
 * Mantém o contrato puro: o kit NÃO conhece o env do shell (CORS proxy, etc.) —
 * o app fornece o que é específico dele via Context.
 *
 *  - `proxyImageUrl`: reescreve URLs de imagem remota (fotos de veículo, logos)
 *    pro proxy CORS do app. Default é identidade — o kit renderiza sem proxy
 *    quando o provider está ausente (ex.: testes), só perde o workaround de CORS.
 *  - `renderMarkdown`: renderiza o texto introdutório do card (`note`) como
 *    markdown, reusando o renderer canônico do shell (mesmo link/segurança dos
 *    balões do chat). Ausente ⇒ o kit cai para texto puro (storybook/testes).
 *  - `ports`: seam de IO de leitura (ver `GenUiPorts`). Ausente ⇒ null object —
 *    o drawer degrada/não abre. É o ÚNICO ponto de IO dos drawers.
 *  - `telemetry`: port de analytics de produto (`track(name, props)`). Ausente ⇒
 *    null object (`useGenUiTelemetry`) — os cards emitem sem saber se alguém
 *    ouve. O REGISTRY que valida `name`/`props` vive no domain, não no kit.
 */
/** O que o explorer de VOO sabe além dos chips na hora do pedido, e que o
 *  host não alcança de fora (é estado interno do reducer): o corte "só direto"
 *  como booleano (o chip é texto traduzido) e a IDA já escolhida online quando
 *  o pedido nasce na fase da volta do ida-e-volta — vai como referência
 *  guardada no pedido, não emitida. O hotel não passa contexto. */
export interface GenUiOfflineFlightContext {
  readonly onlyDirect: boolean;
  readonly heldOutbound?: ConciergeHeldFlight;
}

/** O port da busca offline (ver `GenUiConfig.offlineRequest`). `profile` são
 *  os chips já formatados que a página mostrou à pessoa — é exatamente isso que
 *  o host anexa ao pedido, sem retradução. */
export type GenUiOfflineRequestPort = {
  readonly onRequest: (profile: readonly string[], flight?: GenUiOfflineFlightContext) => void;
};

export interface GenUiConfig {
  proxyImageUrl: (url: string) => string;
  locale: string;
  renderMarkdown?: (text: string) => ReactNode;
  ports?: GenUiPorts;
  telemetry?: GenUiTelemetryPort;
  /**
   * O slot do CROMO do host para a fila de chips de filtro/ordenação da página
   * determinística no palco estreito (#3140): o nó sob a pílula de busca, na
   * top bar. A página do kit monta os chips nele por portal; ausente (o
   * largo, um host sem o slot), `Filtros` e o ordenador ficam na própria
   * página. É um nó do DOM, e não um componente, porque o estado dos cortes
   * mora no organismo — o host só cede o lugar.
   */
  resultsToolbarSlot?: HTMLElement | null;
  /**
   * A busca OFFLINE pela equipe VOLL no pé das páginas determinísticas de
   * resultado (hotel e voo) — o concierge do legado. O host da bancada é quem
   * envia o pedido (abre a conversa com o perfil) e quem lembra que já enviou
   * nesta busca (`sent`, para a pessoa que volta à lista encontrar o status).
   * Ausente — a casca do chat — o pé da lista termina na sentinela.
   */
  offlineRequest?: GenUiOfflineRequestPort;
  /**
   * O canto DIREITO do chrome do takeover de tela cheia do explorer de voos
   * (#3301): o host põe ali o que a pessoa perde quando o takeover cobre o top
   * bar do shell — no web-chat, a mala da viagem (itinerário e serviços já
   * comprados). Um nó React, e não um componente, porque quem sabe QUAL viagem
   * e como abrir o itinerário é o host; o kit só cede o lugar. Ausente ⇒ o
   * chrome fica como está (o ✕ e a pill).
   */
  takeoverTrailing?: ReactNode;
  /**
   * A trilha do chrome dos takeovers de resultados no desktop (#4003), ao lado
   * do ✕: o host desenha a MESMA trilha da sua barra, com o passo que volta à
   * conversa no lugar do `Início` (`onClose`, o gesto do ✕) e a seção do
   * vertical. Ausente ⇒ só o ✕.
   */
  takeoverTrail?: (
    trail: Readonly<{ vertical: GenUiTakeoverVertical; onClose: () => void }>,
  ) => ReactNode;
}

/** O vertical do takeover de resultados — escolhe a seção da trilha. */
export type GenUiTakeoverVertical = 'flight' | 'hotel' | 'rental' | 'bus';

const DEFAULT_CONFIG: GenUiConfig = {
  locale: 'pt-BR',
  proxyImageUrl: (url) => url,
};

const GenUiConfigContext = createContext<GenUiConfig>(DEFAULT_CONFIG);

/** Sem JSX de propósito — defesa em profundidade, não porque este arquivo
 *  seja alcançável da raiz do pacote HOJE: depois da divisão `i18n.ts` /
 *  `i18n_core.ts`, nada em `src/index.ts` importa `config.js` (o único fio
 *  era `hotel_room_identity_label.ts` → `shared/ui/i18n.ts` → aqui, e agora
 *  esse arquivo importa de `i18n_core.js`, que não conhece este módulo). Mas
 *  a raiz é o contrato de wire que consumidores de BACKEND (agents,
 *  trip-document) compilam sem `--jsx`, e um `.tsx` reintroduzido nessa
 *  cadeia por um import futuro derrubaria esses workspaces com TS6142 (ver
 *  `hotel/shared/nights.ts`, onde a mesma classe de bug já aconteceu).
 *  `createElement` mantém o Provider sem exigir a extensão `.tsx`, então
 *  esse risco fica eliminado de vez em vez de só evitado por convenção. */
export function GenUiConfigProvider({
  config,
  children,
}: {
  readonly config: Partial<GenUiConfig>;
  readonly children: ReactNode;
}): ReactNode {
  const value = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  return createElement(GenUiConfigContext.Provider, { value }, children);
}

export function useGenUiConfig(): GenUiConfig {
  return useContext(GenUiConfigContext);
}
